/**
 * The correspondent drilldown's single-document upload slot (drop zone →
 * progress → file card) and the upload hook behind it. Shared by the
 * Onboarding Checklist's prospect questionnaire and the Profile tab's executed
 * MLPA, so both upload the same way.
 */
import { useRef, useState } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";

import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { useCorrespondentsApi } from "../../../hooks/useCorrespondentsHook";
import { slate, status as statusColors, teal } from "../../../theme/colors";
import { FIELD_TEXT } from "../../../theme/formStyles";

/**
 * Upload one file to POST /correspondents/{cid}/documents/upload with a
 * progress readout. `upload(file, submission)` resolves to the updated
 * correspondent (or null on failure, with `error` set).
 */
export const useCorrespondentUpload = (cid) => {
  const correspondentsApi = useCorrespondentsApi();
  const [uploading, setUploading] = useState(null); // { name, size, pct }
  const [error, setError] = useState(null);

  const upload = async (file, submission) => {
    setError(null);
    setUploading({ name: file.name, size: file.size, pct: null });
    try {
      return await correspondentsApi.uploadDocument(cid, file, submission, {
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploading((u) => (u ? { ...u, pct } : u));
        },
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed.");
      return null;
    } finally {
      setUploading(null);
    }
  };

  return { upload, uploading, error };
};

export const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export const DOC_ACCEPT =
  ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// File-type tile on the uploaded card.
const FileTypeTile = ({ name }) => {
  const ext = String(name || "").split(".").pop().toLowerCase();
  const isPdf = ext === "pdf";
  const isWord = ext === "doc" || ext === "docx";
  const tone = isPdf
    ? statusColors.error
    : isWord
      ? statusColors.info
      : statusColors.neutral;
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "8px",
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        background: tone.bg,
        color: tone.fg,
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.02em",
      }}
    >
      {isPdf ? "PDF" : isWord ? "DOC" : ext.slice(0, 4).toUpperCase() || "FILE"}
    </Box>
  );
};

const UploadTile = () => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: "8px",
      flexShrink: 0,
      display: "grid",
      placeItems: "center",
      background: slate[100],
      color: slate[600],
    }}
  >
    <FileUploadOutlined sx={{ fontSize: 18 }} />
  </Box>
);

/**
 * A one-document "on file" slot — three states in one box:
 * - empty: a dashed drop zone ("Drop file or browse"),
 * - uploading: the file name, its size and a progress bar,
 * - on file: the document as a card with preview / replace (+ `extra`, e.g.
 *   a version or status chip, before the actions).
 * The caller does the upload (see `useCorrespondentUpload`). There's no
 * remove action: the backend has no route to delete a correspondent document.
 * Used by the Onboarding Checklist (questionnaire) and Profile (MLPA) tabs.
 */
export const FileSlot = ({
  doc,
  uploading,
  error,
  onFile,
  onPreview,
  accept = DOC_ACCEPT,
  hint = "PDF or Word",
  fallbackName = "Document",
  extra,
}) => {
  const [over, setOver] = useState(false);
  const inputRef = useRef(null);
  const browse = () => inputRef.current?.click();
  const picker = (
    <input
      ref={inputRef}
      type="file"
      hidden
      accept={accept}
      onChange={(e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (f) onFile(f);
      }}
    />
  );
  const dashed = {
    border: `1.5px dashed ${over ? teal[400] : slate[300]}`,
    borderRadius: "10px",
    background: over ? teal[50] : "#fff",
    px: 1.75,
    py: 1.75,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    transition: "background-color .12s, border-color .12s",
  };

  let body;
  if (uploading) {
    body = (
      <Box sx={dashed}>
        <UploadTile />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: 14,
              color: slate[900],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Uploading {uploading.name}…
          </Box>
          <Box sx={{ fontSize: 12.5, color: slate[500] }}>
            {fmtBytes(uploading.size)}
          </Box>
          <LinearProgress
            variant={uploading.pct == null ? "indeterminate" : "determinate"}
            value={uploading.pct ?? 0}
            sx={{
              mt: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: slate[200],
              "& .MuiLinearProgress-bar": {
                backgroundColor: teal[600],
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </Box>
    );
  } else if (doc) {
    const name = doc.file_display_name || doc.file_name_as_loaded || fallbackName;
    const iconBtn = { color: slate[400], "&:hover": { color: slate[700] } };
    body = (
      <Box
        sx={{
          border: `1px solid ${slate[200]}`,
          borderRadius: "10px",
          background: "#fff",
          px: 1.75,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <FileTypeTile name={doc.file_name_as_loaded || name} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            component="button"
            type="button"
            onClick={() => onPreview(doc)}
            sx={{
              display: "block",
              maxWidth: "100%",
              border: "none",
              background: "none",
              p: 0,
              font: "inherit",
              fontSize: 14,
              color: slate[900],
              textAlign: "left",
              cursor: "pointer",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": { color: teal[700], textDecoration: "underline" },
            }}
          >
            {name}
          </Box>
          <Box sx={{ fontSize: 12.5, color: slate[500] }}>
            {[fmtBytes(doc.size), formatDateTime(doc.uploadDate, "MMM d")]
              .filter(Boolean)
              .join(" · ")}
          </Box>
        </Box>
        {extra}
        <Tooltip title="Preview">
          <IconButton size="small" aria-label="Preview" onClick={() => onPreview(doc)} sx={iconBtn}>
            <VisibilityOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Replace with a new file">
          <IconButton size="small" aria-label="Replace" onClick={browse} sx={iconBtn}>
            <AutorenewRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  } else {
    body = (
      <Box
        role="button"
        tabIndex={0}
        onClick={browse}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && browse()}
        sx={{
          ...dashed,
          cursor: "pointer",
          "&:hover": { borderColor: teal[400] },
          "&:focus-visible": { outline: `2px solid ${teal[200]}`, outlineOffset: 2 },
        }}
      >
        <UploadTile />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: 14, color: slate[700] }}>
            Drop file or{" "}
            <Box component="span" sx={{ color: teal[700], fontWeight: 500 }}>
              browse
            </Box>
          </Box>
          <Box sx={{ mt: "3px", fontSize: 11, color: slate[500] }}>{hint}</Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onDragOver={(e) => {
        if (uploading) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f && !uploading) onFile(f);
      }}
    >
      {picker}
      {body}
      {error && (
        <Box sx={{ ...FIELD_TEXT.helper, color: statusColors.error.fg }}>{error}</Box>
      )}
    </Box>
  );
};

